import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Tabs, Typography, Spin, Alert, Button, Space, Table, Tooltip, Modal, message, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  RobotOutlined,
  ToolOutlined,
  GlobalOutlined,
  LockOutlined,
  IdcardOutlined,
  FileTextOutlined,
  ApiOutlined,
  ReadOutlined,
  TeamOutlined,
  SettingOutlined,
  DeploymentUnitOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  CheckSquareOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  HeartOutlined,
  AppstoreOutlined,
  ProfileOutlined,
  AudioOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { AgentBriefingTab } from '@/features/briefing-context/AgentBriefingTab';
import { AgentEvaluationTab } from '@/features/validations/AgentEvaluationTab';
import { EvaluateThisAgentButton, LatestEvaluationCard } from '@/features/scenario-intelligence/workspace/LatestEvaluationCard';
import { Link, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import { AgentStatusPill } from '@/components/agentStatus';
import { AgentStatusControl, AgentStatusHistory } from './AgentStatusControl';
import type {
  AgentDefinition,
  AiSkill,
  AiA2AAgentSkill,
  AiPlugin,
  AiAgentKnowledge,
  AiAgentConsultingPeer,
} from '@/api/types';
import { colors } from '@/theme/tokens';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { trackEvent } from '@/lib/telemetry';
import { PeerEditorDrawer } from './PeerEditorDrawer';
import { PinPromptDrawer } from './PinPromptDrawer';
import { SkillRouterTile } from './SkillRouterTile';
import { AudienceTile } from './AudienceTile';
import { SkillContentDrawer } from './SkillContentDrawer';
import { SkillHistoryDrawer } from './skillHistory/SkillHistoryDrawer';
import { FoundryVariantsCard } from './FoundryVariantsCard';
import { InstructionHistoryDrawer } from './InstructionHistoryDrawer';
import { InstructionEditorDrawer } from './InstructionEditorDrawer';
import { InstructionDetailDrawer } from './InstructionDetailDrawer';
import { PromptReviewDrawer } from '@/features/prompts/PromptReviewDrawer';
import { TestPromptsTab } from './TestPromptsTab';
import { PromptLibraryTab } from './PromptLibraryTab';
import { OutputFormatTab } from './OutputFormatTab';
import { LinkKnowledgeDrawer } from './LinkKnowledgeDrawer';
import { LinkToolsDrawer } from './LinkToolsDrawer';
import { isGlobalTool, audiencesOf, reachSummary } from '@/features/tools/toolMeta';
import { LinkPhraseBucketsDrawer } from './LinkPhraseBucketsDrawer';
import { PhraseListEnabledTile } from './PhraseListEnabledTile';
import type { AgentPhraseGroupView } from '@/api/phraseLists';
import { AgentColorEditorDrawer } from './AgentColorEditorDrawer';
import { ReasoningEffortCard } from './ReasoningEffortCard';
import { ModelSelectionCard } from './ModelSelectionCard';
import { ProvisioningHealthDrawer } from './ProvisioningHealthDrawer';
import { AgentCategoryEditor } from './AgentCategoryEditor';
import { switchPractice } from '@/components/practiceSwitch';
import type { ForeignAgent } from '@/stores/AgentDetailStore';

const { Text } = Typography;

// Detail / editor view for one agent. Tabs match the prior mockup's vision —
// Identity, Instructions, AI Brain, Tools, Knowledge, Procedures, Discoverable
// abilities, Peers, Advanced. Read-only in this sprint; the structure communicates
// product surface and gives users a complete mental model.

export const AgentDetailPage = observer(function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : null;
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [healthOpen, setHealthOpen] = useState<boolean>(false);

  useEffect(() => {
    if (numericId != null) void detail.loadFor(numericId);
    return () => detail.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId]);

  if (numericId == null || Number.isNaN(numericId)) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<RobotOutlined />}
          title="Invalid agent id"
          description="Pick an agent from the roster to view its configuration."
          action={
            <Link to="/agents">
              <Button type="primary">All agents</Button>
            </Link>
          }
        />
      </SectionCard>
    );
  }

  const agent = detail.agent;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Space size={4}>
        <Link to="/agents">
          <Button type="text" size="small" icon={<ArrowLeftOutlined />}>
            All agents
          </Button>
        </Link>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          loading={detail.isLoadingAgent}
          onClick={() => detail.refresh()}
        >
          Refresh
        </Button>
        <Tooltip title="Verify every layer of the agent's stack — DB row, Foundry registration, skills, knowledge bases, peer wirings, test prompts, scenarios.">
          <Button
            type="text"
            size="small"
            icon={<HeartOutlined />}
            onClick={() => setHealthOpen(true)}
            disabled={!agent}
          >
            Provisioning health
          </Button>
        </Tooltip>
      </Space>

      {detail.isLoadingAgent && !agent && (
        <SectionCard>
          <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        </SectionCard>
      )}

      {detail.agentError && (
        <Alert
          type="error"
          message="Failed to load agent"
          description={detail.agentError}
          showIcon
          closable
        />
      )}

      {detail.foreign && <ForeignAgentNotice foreign={detail.foreign} />}

      <ProvisioningHealthDrawer
        open={healthOpen}
        agentId={agent?.id ?? null}
        onClose={() => setHealthOpen(false)}
      />

      {agent && (
        <>
          <AgentHero agent={agent} isDark={isDark} onColorSaved={() => detail.refresh()} />

          <KpiRow onJumpToTab={setActiveTab} />

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarStyle={{ marginBottom: 0, paddingLeft: 4 }}
            items={[
              {
                key: 'overview',
                label: <TabLabel icon={<IdcardOutlined />} text="Overview" />,
                children: <OverviewTab />,
              },
              {
                key: 'instructions',
                label: <TabLabel icon={<FileTextOutlined />} text="Instructions" />,
                children: <InstructionsTab />,
              },
              {
                key: 'brain',
                label: <TabLabel icon={<CodeOutlined />} text="AI Brain" />,
                children: <BrainTab />,
              },
              {
                key: 'tools',
                label: <TabLabel icon={<ApiOutlined />} text="Tools" count={detail.overview?.plugins?.length} />,
                children: <ToolsTab />,
              },
              {
                key: 'knowledge',
                label: <TabLabel icon={<ReadOutlined />} text="Knowledge" count={detail.knowledge.length} />,
                children: <KnowledgeTab />,
              },
              {
                key: 'phrase-lists',
                label: <TabLabel icon={<AudioOutlined />} text="Phrase Lists" count={detail.phraseGroups.length} />,
                children: <PhraseListsTab />,
              },
              {
                key: 'skills',
                label: <TabLabel icon={<ToolOutlined />} text="Skills" count={detail.proceduralSkills.length} />,
                children: <ProceduresTab />,
              },
              {
                key: 'a2a',
                label: <TabLabel icon={<GlobalOutlined />} text="Discoverable abilities" count={detail.a2aSkills.length} />,
                children: <A2ATab />,
              },
              {
                key: 'peers',
                label: <TabLabel icon={<TeamOutlined />} text="Peers" count={detail.peers.length} />,
                children: <PeersTab />,
              },
              {
                // Patient Briefing — the agent's linked briefings (one or more
                // BriefingContext rows whose composition fans out into the
                // system-prompt patient-state block every turn). The
                // AgentBriefingTab component owns its own fetch / list state;
                // no AgentDetailStore wiring needed.
                key: 'patient-briefing',
                label: <TabLabel icon={<ProfileOutlined />} text="Patient Briefing" />,
                children: numericId != null ? <AgentBriefingTab agentId={numericId} /> : null,
              },
              {
                key: 'output-format',
                label: <TabLabel icon={<AppstoreOutlined />} text="Output Format" />,
                children: numericId != null ? <OutputFormatTab agentId={numericId} /> : null,
              },
              {
                key: 'prompts',
                label: (
                  <TabLabel
                    icon={<ThunderboltOutlined />}
                    text="Prebuilt prompts"
                    count={detail.pinnedPrompts.length}
                  />
                ),
                children: <PrebuiltPromptsTab />,
              },
              {
                // EVAL-06: is the agent, as it is now, doing its job — the headline, each scenario's standing on the
                // current version, and its run requests. The component owns its own fetches.
                key: 'evaluation',
                label: <TabLabel icon={<ExperimentOutlined />} text="Evaluation" />,
                children: numericId != null ? <AgentEvaluationTab agentId={numericId} /> : null,
              },
              {
                key: 'test-prompts',
                label: <TabLabel icon={<ExperimentOutlined />} text="Test prompts" />,
                children: <TestPromptsTab />,
              },
              {
                key: 'prompt-library',
                label: <TabLabel icon={<MessageOutlined />} text="Prompt library" />,
                children: <PromptLibraryTab />,
              },
              {
                key: 'advanced',
                label: <TabLabel icon={<SettingOutlined />} text="Advanced" />,
                children: <AdvancedTab />,
              },
              {
                key: 'deploy',
                label: <TabLabel icon={<DeploymentUnitOutlined />} text="Deploy" />,
                children: <DeployTab />,
              },
            ]}
          />
        </>
      )}
    </div>
  );
});

// ── Hero ─────────────────────────────────────────────────────────────────────

/**
 * The URL names an agent another practice owns. Nothing of it is shown or writable here — the breadcrumb says which
 * practice is selected, and this says whose agent it is and how to get to it.
 */
const ForeignAgentNotice = observer(function ForeignAgentNotice({ foreign }: { foreign: ForeignAgent }) {
  const { merchantContextStore: ctx } = useStore();
  const owner = ctx.merchants.find((m) => m.id === foreign.ownerMerchantId);
  const ownerName = owner?.title ?? `practice #${foreign.ownerMerchantId}`;
  const current = ctx.selectedMerchant?.title ?? 'the selected practice';
  return (
    <SectionCard>
      <BrandedEmpty
        icon={<RobotOutlined />}
        title={`${foreign.displayName || `Agent #${foreign.agentId}`} belongs to ${ownerName}`}
        description={`You're working in ${current}, so it isn't opened here. Switch to ${ownerName} to view or change it.`}
        action={
          <Space wrap>
            {owner && (
              <Button type="primary" onClick={() => void switchPractice(ctx, owner.id)}>
                Switch to {ownerName}
              </Button>
            )}
            <Link to="/agents">
              <Button>{`${current}'s agents`}</Button>
            </Link>
          </Space>
        }
      />
    </SectionCard>
  );
});

function AgentHero({ agent, isDark, onColorSaved }: { agent: AgentDefinition; isDark: boolean; onColorSaved?: () => void }) {
  const heroBg = isDark
    ? `linear-gradient(135deg, ${colors.ink[700]}40 0%, ${colors.violet[700]}30 50%, ${colors.slate[800]} 100%)`
    : `linear-gradient(135deg, ${colors.ink[50]} 0%, ${colors.violet[50]} 50%, #FFFFFF 100%)`;

  // Color editor drawer state. Drawer lives inside the hero so the click
  // target (the swatch) is visually adjacent to where it opens.
  const [colorOpen, setColorOpen] = useState(false);
  const swatchColor = agent.color || colors.slate[400];

  return (
    <div
      style={{
        padding: '22px 24px',
        borderRadius: 16,
        background: heroBg,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        // Use the agent's color as a left accent strip — gives the hero a
        // strong per-agent visual identity without dominating the layout.
        borderLeft: agent.color
          ? `5px solid ${agent.color}`
          : `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 18,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${
            isDark ? colors.violet[600] + '55' : colors.violet[200] + '55'
          } 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div style={{ flex: '1 1 260px', minWidth: 0, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Clickable color swatch — opens the AgentColorEditorDrawer */}
          <Tooltip title={agent.color ? `Color: ${agent.color} — click to change` : 'No color set — click to pick'}>
            <button
              type="button"
              aria-label="Edit agent color"
              onClick={() => setColorOpen(true)}
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                background: swatchColor,
                border: `2px solid ${isDark ? colors.slate[700] : '#FFFFFF'}`,
                boxShadow: agent.color
                  ? `0 0 0 1px ${swatchColor}55, 0 1px 3px rgba(15,23,42,0.18)`
                  : `0 0 0 1px ${colors.slate[300]}, 0 1px 3px rgba(15,23,42,0.10)`,
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
                transition: 'transform 100ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          </Tooltip>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: isDark ? colors.slate[50] : colors.slate[900],
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
            }}
          >
            {agent.displayName}
          </div>
        </div>
        {agent.a2AAgentName && (
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              color: isDark ? colors.slate[400] : colors.slate[500],
              marginTop: 4,
            }}
          >
            {agent.a2AAgentName}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {agent.merchantId == null && <StatusPill tone="violet">Global</StatusPill>}
          {agent.patientApp && <StatusPill tone="ink">Patient</StatusPill>}
          {agent.merchantApp && <StatusPill tone="violet">Care team</StatusPill>}
          {agent.enableSkillRouter ? (
            <StatusPill tone="green" icon={<ThunderboltOutlined />}>Router on</StatusPill>
          ) : (
            <StatusPill tone="slate">Router off</StatusPill>
          )}
          <AgentStatusPill agent={agent} />
          <StatusPill tone="slate">#{agent.id}</StatusPill>
        </div>
      </div>

      {/* Scenario Intelligence screen 01, frame B: opens the Overview with this agent and the selected practice chosen. */}
      <div style={{ position: 'relative', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {/* Evaluate, then make it Active once the evaluations are good (founder, 2 Nov 2026). */}
        <AgentStatusControl agent={agent} />
        <EvaluateThisAgentButton agentId={agent.id} />
      </div>

      <AgentColorEditorDrawer
        open={colorOpen}
        agent={agent}
        onClose={() => setColorOpen(false)}
        onSaved={() => {
          setColorOpen(false);
          if (onColorSaved) onColorSaved();
        }}
      />
    </div>
  );
}

// ── KPI Row ──────────────────────────────────────────────────────────────────

const KpiRow = observer(function KpiRow({
  onJumpToTab,
}: {
  onJumpToTab: (key: string) => void;
}) {
  const { agentDetailStore: detail } = useStore();

  const procedureCount = detail.proceduralSkills.length;
  const a2aCount = detail.a2aSkills.length;

  // Audience is rendered by AudienceTile (interactive — patient/care toggles
  // inline) so the formerly-static `audienceValue` block was removed.

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
      }}
    >
      <KpiTile
        accent="ink"
        label="Skills"
        // Empty state — render "None" in medium so it visually matches the
        // populated cards' weight; "—" alone reads as dead space.
        value={procedureCount === 0 ? 'None' : procedureCount}
        valueSize={procedureCount === 0 ? 'medium' : 'large'}
        hint="Manage on Skills tab →"
        icon={<ToolOutlined />}
        helpTopic="procedures.skills"
        onClick={() => onJumpToTab('skills')}
      />
      <KpiTile
        accent="violet"
        label="Discoverable abilities"
        value={a2aCount === 0 ? 'None' : a2aCount}
        valueSize={a2aCount === 0 ? 'medium' : 'large'}
        hint="Manage on Discoverable abilities tab →"
        icon={<GlobalOutlined />}
        helpTopic="agents.discoverable-abilities"
        onClick={() => onJumpToTab('a2a')}
      />
      <AudienceTile />
      <SkillRouterTile />
      <PhraseListEnabledTile />
    </div>
  );
});

// ── Tab label ────────────────────────────────────────────────────────────────

function TabLabel({ icon, text, count }: { icon: React.ReactNode; text: string; count?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {icon}
      <span>{text}</span>
      {count != null && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '0 6px',
            borderRadius: 999,
            background: colors.slate[100],
            color: colors.slate[600],
            fontFamily: "'JetBrains Mono', monospace",
            lineHeight: '16px',
            minWidth: 18,
            textAlign: 'center',
            display: 'inline-block',
          }}
        >
          {count}
        </span>
      )}
    </span>
  );
}

// ── Tab content ──────────────────────────────────────────────────────────────

const OverviewTab = observer(function OverviewTab() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const a = detail.agent;
  const isDark = themeStore.mode === 'dark';
  if (!a) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
      {/* The latest evaluation in the Overview's five counts (Scenario Intelligence screen 01, frame B). */}
      <LatestEvaluationCard agentId={a.id} />
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 14 }}>
        <SectionCard title="Identity" subtitle="Core fields stored on ai.AiAgent">
          <DefList
            rows={[
              { label: 'Display name', value: a.displayName },
              { label: 'A2A agent name', value: a.a2AAgentName ? <Mono>{a.a2AAgentName}</Mono> : '—' },
              { label: 'Description', value: a.description ?? '—', span: 2 },
              {
                label: 'Category',
                // Inline editor — read mode shows the colored Tag, click
                // pencil to swap to a Select (with a "+ New category"
                // footer that opens a small Modal). Persists via
                // updateAgent(...) immediately on pick; no separate Save.
                value: (
                  <AgentCategoryEditor
                    agent={a}
                    isDark={isDark}
                    onSaved={(next) => detail.applyAgentPatch(next)}
                  />
                ),
                span: 2,
              },
              { label: 'Agent id', value: <Mono>#{a.id}</Mono> },
              { label: 'Merchant id', value: a.merchantId == null ? <StatusPill tone="violet">Global</StatusPill> : <Mono>#{a.merchantId}</Mono> },
              { label: 'Created', value: formatDate(a.createdDate) ?? '—' },
              { label: 'Updated', value: formatDate(a.updatedDate) ?? '—' },
            ]}
          />
        </SectionCard>
        <SectionCard title="At a glance" subtitle="Audience + visibility">
          <DefList
            rows={[
              { label: 'Audience', value: <AudienceCell agent={a} /> },
              { label: 'Status', value: <AgentStatusPill agent={a} /> },
              { label: 'Skill router', value: a.enableSkillRouter ? <StatusPill tone="green">Enabled</StatusPill> : <StatusPill tone="slate">Disabled</StatusPill> },
              { label: 'Consultation', value: a.enableConsultation ? <StatusPill tone="ink">{`Depth ${a.maxConsultationDepth ?? '?'}`}</StatusPill> : <StatusPill tone="slate">Off</StatusPill> },
            ]}
            singleColumn
          />
          <div style={{ marginTop: 14 }}>
            <Text strong style={{ fontSize: 12.5 }}>Status history</Text>
            <div style={{ marginTop: 6 }}>
              <AgentStatusHistory agent={a} />
            </div>
          </div>
        </SectionCard>
      </div>
      {/* Reasoning effort — full-width row beneath the identity cards.
          Authors see Low/Medium/High descriptions inline while picking,
          plus a live "what would actually fire at runtime" indicator and
          a one-click history drawer for the audit trail. */}
      <ReasoningEffortCard agentId={a.id} isDark={isDark} />
    </div>
  );
});

const InstructionsTab = observer(function InstructionsTab() {
  const { agentDetailStore: detail } = useStore();
  const agent = detail.agent;
  const prompts = detail.overview?.systemPrompts ?? [];
  const capabilities = detail.overview?.capabilities ?? [];

  // The agent's PRIMARY instruction (ai.AiAgent.Instructions). This is the
  // agent's "constitution" — its own system-prompt body, separate from
  // capability-contributed segments. Surfaced first because it's the
  // dominant authoring surface for most clinical agents.
  const primaryInstruction = (agent?.instructions ?? '').trim();

  // Editor drawer — opens when the user clicks "Edit with AI co-pilot" in the
  // Primary instruction panel. The drawer pulls overview (capabilities +
  // system prompts) so its "find redundancy" mode and effective-prompt
  // preview have something to work with.
  const [editorOpen, setEditorOpen] = useState(false);
  // History drawer — also reachable from inside the editor, but exposing
  // it here means an author can audit edits without committing to an
  // edit session. Same drawer component, same data source.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Detail drawer — 90%-width read-only deep-dive on the full primary
  // instruction. Replaces the old inline "Show full" toggle so a 30 KB
  // body doesn't drown the page.
  const [detailOpen, setDetailOpen] = useState(false);
  // Review-with-AI drawer — kicks off the prompt-review agent against
  // ai.AiAgent.Instructions. Same drawer component the Prebuilt prompts
  // page uses, just scoped to PromptKind="AgentInstructions".
  const [reviewOpen, setReviewOpen] = useState(false);

  // SystemPrompt entity stores the body in BaseInstructions. Title is the
  // human-readable label. Comments is supplemental context.
  const blocks = prompts
    .map((p) => ({
      id: p.id,
      label: p.title?.trim() || p.name?.trim() || `Prompt #${p.id}`,
      body: (p.baseInstructions ?? '').trim(),
      comments: p.comments?.trim() ?? '',
      tags: p.tags ?? [],
    }))
    .filter((b) => b.body.length > 0 || b.comments.length > 0);

  const segmentChars = blocks.reduce((acc, b) => acc + b.body.length, 0);
  const totalChars = primaryInstruction.length + segmentChars;

  if (detail.isLoadingOverview && prompts.length === 0 && !agent) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.overviewError) {
    return (
      <SectionCard>
        <Alert
          type="error"
          message="Failed to load instructions"
          description={detail.overviewError}
          showIcon
        />
      </SectionCard>
    );
  }
  if (!primaryInstruction && blocks.length === 0 && capabilities.length === 0) {
    return (
      <SectionCard
        title="Instructions"
        subtitle="This agent has no primary instruction, capabilities, or prompt segments configured."
      >
        <BrandedEmpty
          icon={<FileTextOutlined />}
          title="Nothing to compose"
          description="Instructions are built from the agent's primary text plus linked capabilities. Author them in the agent record (ai.AiAgent.Instructions) and on linked capabilities."
        />
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
      {/* Composition explainer */}
      <CompositionExplainer
        capabilityCount={capabilities.length}
        segmentCount={blocks.length}
        totalChars={totalChars}
        primaryChars={primaryInstruction.length}
        capabilities={capabilities}
      />

      {/* Primary instruction — the agent's own Instructions field */}
      {primaryInstruction.length > 0 ? (
        <PrimaryInstructionPanel
          body={primaryInstruction}
          onEdit={() => setEditorOpen(true)}
          onShowHistory={() => setHistoryOpen(true)}
          onShowDetail={() => setDetailOpen(true)}
          onReviewWithAI={agent ? () => setReviewOpen(true) : undefined}
        />
      ) : agent ? (
        // Fresh agent with no instruction yet — give the user a way to start
        // authoring without staring at a blank panel.
        <SectionCard
          title="Primary instruction"
          subtitle="No primary instruction yet — author one with the AI co-pilot."
          actions={
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditorOpen(true)}>
              Author with AI
            </Button>
          }
        >
          <BrandedEmpty
            icon={<FileTextOutlined />}
            title="No instruction yet"
            description="Click 'Author with AI' to start the smart editor — it knows your linked capabilities and skills."
          />
        </SectionCard>
      ) : null}

      <InstructionEditorDrawer
        open={editorOpen}
        agent={agent ?? null}
        overview={detail.overview ?? null}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          // Trigger a full refresh so the panel re-renders with the saved
          // body and all derived numbers (token estimate, segment count) are
          // recomputed from the canonical record.
          detail.refresh();
        }}
      />

      {/* Standalone history drawer — same component the editor toolbar
          uses. Opens directly from the Instructions tab so authors can
          read-only-audit edits without entering an edit session. Restoring
          a version triggers a full overview refresh because the agent
          record changes underneath this view. */}
      <InstructionHistoryDrawer
        open={historyOpen}
        agent={agent ?? null}
        currentInstructions={primaryInstruction}
        onClose={() => setHistoryOpen(false)}
        onRestored={() => {
          setHistoryOpen(false);
          detail.refresh();
        }}
      />

      {/* 90%-width read-only deep dive. Hosts Edit / Copy / Review-with-AI
          / Download in its sticky toolbar; right rail shows an auto-extracted
          outline + at-a-glance stats. Mounted on the parent so closing the
          detail view doesn't tear down the editor or history drawer state. */}
      <InstructionDetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        body={primaryInstruction}
        contextLabel={agent?.displayName ?? null}
        stableId="ai.AiAgent.Instructions"
        downloadFilename={
          agent
            ? `${(agent.canonicalName || agent.displayName || 'agent').replace(/\s+/g, '-').toLowerCase()}-instructions.md`
            : 'agent-instructions.md'
        }
        onEdit={() => {
          setDetailOpen(false);
          setEditorOpen(true);
        }}
        onReviewWithAI={agent ? () => setReviewOpen(true) : undefined}
      />

      {/* Review with AI — runs the prompt-review agent against
          ai.AiAgent.Instructions. Same drawer the Prebuilt prompts list uses,
          just scoped to a different PromptKind. On Accept the backend
          AIAgentConfigManager.Update path Foundry-syncs the new body and
          records ai.AgentInstructionHistory; we trigger detail.refresh()
          so the panel re-renders with the accepted text. */}
      {agent && (
        <PromptReviewDrawer
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          promptKind="AgentInstructions"
          promptId={agent.id}
          promptLabel={agent.displayName ?? agent.canonicalName ?? `Agent #${agent.id}`}
          onApplied={() => {
            detail.refresh();
          }}
        />
      )}

      {/* Segments */}
      {blocks.length > 0 && (
        <SectionCard
          title={
            <span>
              Prompt segments{' '}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '1px 7px',
                  borderRadius: 999,
                  background: colors.slate[100],
                  color: colors.slate[600],
                  fontFamily: "'JetBrains Mono', monospace",
                  marginLeft: 4,
                }}
              >
                {blocks.length}
              </span>
            </span>
          }
          subtitle={`Concatenated into the agent's effective system prompt at runtime · ${totalChars.toLocaleString()} chars total`}
          noPadding
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {blocks.map((b, i) => (
              <SegmentRow key={b.id} block={b} index={i + 1} isLast={i === blocks.length - 1} />
            ))}
          </div>
        </SectionCard>
      )}

      {blocks.length === 0 && (
        <SectionCard>
          <BrandedEmpty
            icon={<FileTextOutlined />}
            title="No prompt-segment text yet"
            description="The capabilities are linked but their BaseInstructions bodies are empty. Capability authoring lives elsewhere in the platform today; we surface them read-only here."
          />
        </SectionCard>
      )}
    </div>
  );
});

// ── Composition explainer ────────────────────────────────────────────────────

const CompositionExplainer = observer(function CompositionExplainer({
  capabilityCount,
  segmentCount,
  totalChars,
  primaryChars,
  capabilities,
}: {
  capabilityCount: number;
  segmentCount: number;
  totalChars: number;
  primaryChars: number;
  capabilities: { id: number; title: string; description?: string; isActive?: boolean }[];
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const heroBg = isDark
    ? `linear-gradient(135deg, ${colors.violet[700]}26 0%, ${colors.slate[800]} 100%)`
    : `linear-gradient(135deg, ${colors.violet[50]} 0%, #FFFFFF 100%)`;
  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const valueColor = isDark ? colors.slate[50] : colors.slate[900];

  return (
    <div
      style={{
        background: heroBg,
        border: `1px solid ${isDark ? colors.slate[700] : colors.violet[100]}`,
        borderRadius: 12,
        padding: '18px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.violet[200]}40 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: labelColor,
            marginBottom: 6,
          }}
        >
          How instructions compose
        </div>
        <div style={{ fontSize: 14, color: valueColor, lineHeight: 1.6, maxWidth: 720 }}>
          The agent's effective system prompt is built at runtime by concatenating its{' '}
          <strong>primary instruction</strong> with the <strong>BaseInstructions</strong> from
          every linked capability. Capabilities are reusable feature modules; each carries one
          or more prompt segments below.
        </div>

        {/* Numbers strip */}
        <div
          style={{
            display: 'flex',
            gap: 28,
            marginTop: 14,
            flexWrap: 'wrap',
          }}
        >
          <Stat
            label="Primary instruction"
            value={primaryChars > 0 ? `${primaryChars.toLocaleString()} chars` : '—'}
            hint={primaryChars > 0 ? `≈ ${Math.round(primaryChars / 4).toLocaleString()} tokens` : 'not set'}
          />
          <Stat label="Capabilities" value={capabilityCount} />
          <Stat label="Prompt segments" value={segmentCount} />
          <Stat
            label="Effective prompt"
            value={`${totalChars.toLocaleString()} chars`}
            hint={`≈ ${Math.round(totalChars / 4).toLocaleString()} tokens`}
          />
        </div>

        {/* Capability pills */}
        {capabilities.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: labelColor,
                marginBottom: 8,
              }}
            >
              Linked capabilities
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {capabilities.map((c) => (
                <Tooltip key={c.id} title={c.description || c.title}>
                  <span>
                    <StatusPill tone="violet">
                      {c.title || `Capability #${c.id}`}
                    </StatusPill>
                  </span>
                </Tooltip>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const Stat = observer(function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: isDark ? colors.slate[50] : colors.slate[900],
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
            marginTop: 2,
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
});

// ── Primary instruction panel — the agent's own ai.AiAgent.Instructions ───
//
// This is the agent's "constitution" — its dominant authoring surface.
// We surface it FIRST on the Instructions tab because for most clinical
// agents it's where 95% of the actual prompt content lives (the
// capability segments are typically thin shared modules).
//
// UX choices:
//   • Collapsed by default when > ~1200 chars. Long instructions are the
//     norm (12-30k chars), so showing the full body always pushes the
//     capability segments below the fold.
//   • Copy-to-clipboard for one-click extraction into prompt-engineering
//     tools.
//   • Char count + token estimate match the explainer numbers above so
//     authors can size up the prompt budget without doing math.
//   • Monospace + preserve-whitespace renders Markdown legibly without
//     pulling in a markdown library.

const PrimaryInstructionPanel = observer(function PrimaryInstructionPanel({
  body,
  onEdit,
  onShowHistory,
  onShowDetail,
  onReviewWithAI,
}: {
  body: string;
  onEdit?: () => void;
  onShowHistory?: () => void;
  /** Opens the 90%-width Detail drawer for the full body + outline. */
  onShowDetail?: () => void;
  /** Opens the Review-with-AI drawer scoped to AgentInstructions. */
  onReviewWithAI?: () => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [copied, setCopied] = useState(false);

  const tokens = Math.round(body.length / 4);
  const lines = body.split('\n').length;

  // Always-collapsed preview — clicking Show full opens the rich Detail
  // drawer instead of expanding inline. Preview = first ~600 chars,
  // rounded to a line break so we don't cut a sentence in half.
  const previewMax = 600;
  let preview = body;
  if (body.length > previewMax) {
    const cut = body.slice(0, previewMax);
    const lastBreak = Math.max(cut.lastIndexOf('\n'), cut.lastIndexOf('. '));
    preview = cut.slice(0, lastBreak > 200 ? lastBreak : previewMax) + '\n…';
  }

  function copy() {
    navigator.clipboard
      .writeText(body)
      .then(() => {
        setCopied(true);
        message.success('Copied primary instruction to clipboard');
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => message.error('Could not copy — clipboard blocked'));
  }

  return (
    <SectionCard
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Primary instruction
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 7px',
              borderRadius: 999,
              background: isDark ? colors.violet[700] + '55' : colors.violet[100],
              color: isDark ? colors.violet[100] : colors.violet[700],
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            ai.AiAgent.Instructions
          </span>
        </span>
      }
      subtitle={
        <span>
          {body.length.toLocaleString()} chars · ≈ {tokens.toLocaleString()} tokens · {lines.toLocaleString()} lines
        </span>
      }
      actions={
        <Space wrap>
          {onEdit && (
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              onClick={onEdit}
            >
              Edit with AI co-pilot
            </Button>
          )}
          {onReviewWithAI && (
            <Tooltip title="Run the prompt-review agent against this instruction. Scores 11 dimensions including clinical accuracy + safety; on Accept, the new body Foundry-syncs and is recorded to ai.AgentInstructionHistory.">
              <Button
                size="small"
                icon={<RobotOutlined />}
                onClick={onReviewWithAI}
              >
                Review with AI
              </Button>
            </Tooltip>
          )}
          {onShowHistory && (
            <Tooltip title="See and restore previous saved versions of this instruction">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={onShowHistory}
              >
                History
              </Button>
            </Tooltip>
          )}
          <Button
            size="small"
            icon={copied ? <CheckSquareOutlined /> : <CodeOutlined />}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          {body.length > 1200 && onShowDetail && (
            <Button size="small" type="primary" onClick={onShowDetail}>
              Show full ({body.length.toLocaleString()} chars)
            </Button>
          )}
        </Space>
      }
    >
      <pre
        style={{
          margin: 0,
          padding: 16,
          borderRadius: 10,
          background: isDark ? colors.slate[900] : colors.slate[50],
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 12.5,
          lineHeight: 1.6,
          color: isDark ? colors.slate[100] : colors.slate[800],
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 320,
          overflow: 'auto',
        }}
      >
        {body.length > 1200 ? preview : body}
      </pre>
    </SectionCard>
  );
});

// ── Single segment row ───────────────────────────────────────────────────────

const SegmentRow = observer(function SegmentRow({
  block,
  index,
  isLast,
}: {
  block: { id: number; label: string; body: string; comments: string; tags: string[] };
  index: number;
  isLast: boolean;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div
      style={{
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 22,
              height: 22,
              padding: '0 7px',
              borderRadius: 999,
              background: isDark ? colors.slate[700] : colors.slate[100],
              color: isDark ? colors.slate[300] : colors.slate[500],
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {index}
          </span>
          <Text strong style={{ fontSize: 13.5 }}>{block.label}</Text>
          {block.tags.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {block.tags.slice(0, 4).map((t) => (
                <StatusPill key={t} tone="violet">{t}</StatusPill>
              ))}
            </span>
          )}
        </div>
        <Text
          style={{
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
            fontFamily: "'JetBrains Mono', monospace",
            whiteSpace: 'nowrap',
          }}
        >
          {block.body.length.toLocaleString()} chars
        </Text>
      </div>

      {block.comments && (
        <Text type="secondary" italic style={{ fontSize: 12, paddingLeft: 32 }}>
          {block.comments}
        </Text>
      )}

      {block.body ? (
        <pre
          style={{
            background: isDark ? colors.slate[900] : colors.slate[50],
            padding: 14,
            borderRadius: 8,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            lineHeight: 1.65,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 360,
            overflow: 'auto',
            margin: 0,
            color: isDark ? colors.slate[200] : colors.slate[800],
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
        >
          {block.body}
        </pre>
      ) : (
        <Text type="secondary" italic style={{ fontSize: 12, paddingLeft: 32 }}>
          (No body — comments only)
        </Text>
      )}
    </div>
  );
});

const BrainTab = observer(function BrainTab() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const a = detail.agent;
  if (!a) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  const foundryVariants = detail.overview?.foundryVariants;

  const compactionPct = a.compactionThresholdPercent ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionCard
        title="Model selection"
        subtitle="Which LLM serves a turn for this agent."
        helpTopic="models.overview"
      >
        {/* Per-agent model binding (ai.AiAgent.ModelId). Current model shown
            prominently, one card per available model, confirm-with-reason
            on change, audit history drawer. */}
        <ModelSelectionCard agentId={a.id} isDark={isDark} />
      </SectionCard>

      <SectionCard
        title="Runtime configuration"
        subtitle="Per-agent knobs that govern how each turn is executed."
      >
        <DefList
          rows={[
            {
              label: 'Compaction strategy',
              value: a.compactionStrategyName ? (
                <StatusPill tone="ink">{a.compactionStrategyName}</StatusPill>
              ) : (
                <Text type="secondary">None — context window is left as-is</Text>
              ),
            },
            {
              label: 'Compaction threshold',
              value:
                compactionPct != null ? (
                  <span><Mono>{compactionPct}%</Mono> of model context</span>
                ) : (
                  <Text type="secondary">Code default (80%)</Text>
                ),
            },
            {
              label: 'Peer consultation',
              value: a.enableConsultation ? (
                <StatusPill tone="green">Enabled — depth ≤ {a.maxConsultationDepth ?? 1}</StatusPill>
              ) : (
                <StatusPill tone="slate">Disabled</StatusPill>
              ),
            },
            {
              label: 'Skill router',
              value: a.enableSkillRouter ? (
                <StatusPill tone="green">Enabled — skills pre-injected per turn</StatusPill>
              ) : (
                <StatusPill tone="slate">Disabled — every skill sent every turn</StatusPill>
              ),
            },
            {
              label: 'Primary Foundry agent',
              value: a.foundryAgentId ? (
                <Mono>{a.foundryAgentId}</Mono>
              ) : (
                <Text type="secondary">Not provisioned on Azure AI Foundry</Text>
              ),
            },
          ]}
          singleColumn
        />
      </SectionCard>

      {/* ── Foundry variants ─────────────────────────────────────────────
          Per-(model, FoundryAgentId) inventory for this agent. Surfaces the
          local↔Foundry mapping, the per-variant drift indicator (against
          the last-synced instructions hash), and the retirement chip from
          the static ModelLifecyclePolicy table. All in one round-trip — no
          live Foundry SDK calls.
          ──────────────────────────────────────────────────────────────── */}
      <SectionCard
        title="Foundry variants"
        subtitle="One Foundry agent version per Foundry-eligible model. Drift means edits haven't been pushed yet."
      >
        <FoundryVariantsCard
          variants={foundryVariants}
          legacyFoundryAgentId={a.foundryAgentId}
        />
      </SectionCard>
    </div>
  );
});

const ToolsTab = observer(function ToolsTab() {
  const { agentDetailStore: detail } = useStore();
  const [linkDrawerOpen, setLinkDrawerOpen] = useState(false);
  const plugins = detail.overview?.plugins ?? [];
  const agentName = detail.agent?.displayName ?? 'this agent';
  const grantedNames = new Set(plugins.map((p) => p.name));

  if (detail.isLoadingOverview && plugins.length === 0) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.overviewError) {
    return (
      <SectionCard>
        <Alert type="error" message="Failed to load tools" description={detail.overviewError} showIcon />
      </SectionCard>
    );
  }

  // Confirm-then-revoke. Plain language: this removes the tool from THIS agent
  // only; the tool itself and every other agent granted it are untouched.
  function confirmRevoke(row: AiPlugin): void {
    Modal.confirm({
      title: `Remove ${row.name} from this agent?`,
      content: (
        <div>
          <p style={{ marginTop: 8 }}>
            This <b>only removes the tool from this agent</b>. The tool itself, and any other
            agent granted it, are <b>not affected</b>.
          </p>
          <p style={{ fontSize: 13, color: colors.slate[500], marginBottom: 0 }}>
            You can grant it again any time from the same screen.
          </p>
        </div>
      ),
      okText: 'Remove tool',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detail.revokePlugin(row.name);
          message.success(`Removed ${row.name} from ${agentName}`);
          trackEvent('AgentPlugin.Revoked', { agentId: detail.agentId ?? -1, pluginName: row.name });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to remove tool');
        }
      },
    });
  }

  const columns: ColumnsType<AiPlugin> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (n: string) => <Mono>{n}</Mono>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (t?: string) => (
        <Tooltip title={t} placement="topLeft" mouseEnterDelay={0.3}>
          <Text type="secondary" style={{ fontSize: 13 }}>{t ?? '—'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Reach',
      key: 'reach',
      width: 190,
      render: (_t, p) => {
        const global = isGlobalTool(p);
        return (
          <Tooltip title={reachSummary(p)}>
            <Space size={6} align="center">
              {global ? (
                <StatusPill tone="amber"><GlobalOutlined style={{ marginRight: 4 }} />Global</StatusPill>
              ) : (
                <StatusPill tone="slate"><LockOutlined style={{ marginRight: 4 }} />Scoped</StatusPill>
              )}
              {global &&
                audiencesOf(p).map((a) => (
                  <Tag key={a} color="gold" style={{ fontSize: 10.5, marginInlineEnd: 0 }}>{a}</Tag>
                ))}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_t, row) =>
        isGlobalTool(row) ? (
          // A global tool isn't granted per-agent, so there's nothing here to
          // remove — say so rather than offer a button that would no-op.
          <Tooltip title="This tool is global — it reaches every agent and can't be removed from just this one.">
            <Button type="text" size="small" disabled>Global</Button>
          </Tooltip>
        ) : (
          <Tooltip title="Remove this tool from the agent (does not delete the tool)">
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => confirmRevoke(row)}>
              Remove
            </Button>
          </Tooltip>
        ),
    },
  ];

  const grantButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => {
        setLinkDrawerOpen(true);
        trackEvent('AgentPlugin.GrantDrawerOpened', { agentId: detail.agentId ?? -1 });
      }}
    >
      Grant tools
    </Button>
  );

  return (
    <>
      <SectionCard
        title="Tools"
        subtitle={
          plugins.length === 0
            ? 'No tools granted yet. Click Grant tools to add one.'
            : `${plugins.length} tool${plugins.length === 1 ? '' : 's'} this agent can call`
        }
        actions={grantButton}
        noPadding={plugins.length > 0}
      >
        {plugins.length === 0 ? (
          <BrandedEmpty
            icon={<ApiOutlined />}
            title="No tools granted"
            description="Tools (plugins) let an agent take action — query data, render a chart, send a message. Grant only what this agent needs."
          />
        ) : (
          <Table<AiPlugin>
            rowKey="id"
            columns={columns}
            dataSource={plugins}
            pagination={false}
            size="middle"
          />
        )}
      </SectionCard>

      <LinkToolsDrawer
        open={linkDrawerOpen}
        onClose={() => setLinkDrawerOpen(false)}
        alreadyGrantedNames={grantedNames}
        agentDisplayName={agentName}
      />
    </>
  );
});

const KnowledgeTab = observer(function KnowledgeTab() {
  const { agentDetailStore: detail } = useStore();
  const [linkDrawerOpen, setLinkDrawerOpen] = useState(false);

  if (detail.isLoadingKnowledge && detail.knowledge.length === 0) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.knowledgeError) {
    return (
      <SectionCard>
        <Alert type="error" message="Failed to load knowledge" description={detail.knowledgeError} showIcon />
      </SectionCard>
    );
  }

  const linkedIds = new Set(detail.knowledge.map((k) => k.knowledgeId));
  const agentName = detail.agent?.displayName ?? 'this agent';

  // Confirm-then-unlink. Plain language: this is just removing the link, not
  // deleting the knowledge base. Other agents using the same KB are unaffected.
  function confirmUnlink(row: AiAgentKnowledge): void {
    const title = row.knowledge?.title ?? `Knowledge #${row.knowledgeId}`;
    Modal.confirm({
      title: `Unlink "${title}" from this agent?`,
      content: (
        <div>
          <p style={{ marginTop: 8 }}>
            This <b>only removes the link</b> between this agent and the knowledge base.
            The knowledge base itself, all of its files, and any other agents using it
            are <b>not affected</b>.
          </p>
          <p style={{ fontSize: 13, color: colors.slate[500], marginBottom: 0 }}>
            You can re-link it any time from the same screen.
          </p>
        </div>
      ),
      okText: 'Unlink',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detail.unlinkKnowledge(row.id);
          message.success(`Unlinked "${title}" from ${agentName}`);
          trackEvent('AgentKnowledge.Unlinked', {
            agentId: detail.agentId ?? -1,
            knowledgeId: row.knowledgeId,
            linkId: row.id,
          });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to unlink knowledge');
        }
      },
    });
  }

  const columns: ColumnsType<AiAgentKnowledge> = [
    {
      title: 'Title',
      key: 'title',
      render: (_t, row) => (
        <div>
          <Text strong style={{ fontSize: 13.5 }}>
            {row.knowledge?.title ?? '—'}
          </Text>
          {row.knowledge?.verified && (
            <span style={{ marginLeft: 8 }}>
              <StatusPill tone="green">Verified</StatusPill>
            </span>
          )}
          {row.knowledge?.description && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.knowledge.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Sources',
      key: 'sources',
      width: 280,
      render: (_t, row) => {
        const k = row.knowledge;
        if (!k) return <Text type="secondary">—</Text>;
        const parts: { label: string; n: number; tone: 'ink' | 'violet' | 'amber' }[] = [
          { label: 'PDF', n: k.pdfCount ?? 0, tone: 'ink' },
          { label: 'Markdown', n: k.markdownCount ?? 0, tone: 'violet' },
          { label: 'Link', n: k.linkCount ?? 0, tone: 'amber' },
        ];
        const total = parts.reduce((a, p) => a + p.n, 0);
        if (total === 0)
          return <Text type="secondary" style={{ fontSize: 12 }}>Empty</Text>;
        return (
          <Space size={4} wrap>
            {parts.filter((p) => p.n > 0).map((p) => (
              <StatusPill key={p.label} tone={p.tone}>{p.n} {p.label}</StatusPill>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Knowledge id',
      dataIndex: 'knowledgeId',
      key: 'knowledgeId',
      width: 130,
      render: (v: number) => <Mono>#{v}</Mono>,
    },
    {
      title: 'Linked',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 140,
      render: (d?: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {d ? new Date(d).toLocaleDateString() : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      render: (_t, row) => (
        <Tooltip title="Unlink this knowledge from the agent (does not delete the knowledge base)">
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => confirmUnlink(row)}
          >
            Unlink
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="Knowledge"
        subtitle={
          detail.knowledge.length === 0
            ? 'No knowledge bases linked yet. Click Link knowledge to attach one.'
            : `${detail.knowledge.length} source${detail.knowledge.length === 1 ? '' : 's'} grounding this agent`
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setLinkDrawerOpen(true);
              trackEvent('AgentKnowledge.LinkDrawerOpened', { agentId: detail.agentId ?? -1 });
            }}
          >
            Link knowledge
          </Button>
        }
        noPadding={detail.knowledge.length > 0}
      >
        {detail.knowledge.length === 0 ? (
          <BrandedEmpty
            icon={<ReadOutlined />}
            title="No knowledge sources linked"
            description="Knowledge bases let this agent quote authoritative material in its responses. Click Link knowledge above to attach one."
          />
        ) : (
          <Table<AiAgentKnowledge>
            rowKey="id"
            columns={columns}
            dataSource={detail.knowledge}
            pagination={false}
            size="middle"
            scroll={{ x: 'max-content' }}
          />
        )}
      </SectionCard>

      <LinkKnowledgeDrawer
        open={linkDrawerOpen}
        onClose={() => setLinkDrawerOpen(false)}
        alreadyLinkedIds={linkedIds}
        agentDisplayName={agentName}
      />
    </>
  );
});

const PhraseListsTab = observer(function PhraseListsTab() {
  const { agentDetailStore: detail } = useStore();
  const [linkDrawerOpen, setLinkDrawerOpen] = useState(false);

  if (detail.isLoadingPhraseGroups && detail.phraseGroups.length === 0) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.phraseGroupsError) {
    return (
      <SectionCard>
        <Alert type="error" message="Failed to load phrase buckets" description={detail.phraseGroupsError} showIcon />
      </SectionCard>
    );
  }

  const linkedIds = new Set(detail.phraseGroups.map((g) => g.groupId));
  const agentName = detail.agent?.displayName ?? 'this agent';
  const enabled = !!detail.agent?.enablePhraseList;

  // Confirm-then-detach. Plain language: this only removes the link, not the
  // bucket. Other agents using the same bucket are unaffected.
  function confirmDetach(row: AgentPhraseGroupView): void {
    Modal.confirm({
      title: `Detach "${row.title}" from this agent?`,
      content: (
        <div>
          <p style={{ marginTop: 8 }}>
            This <b>only removes the link</b> between this agent and the phrase bucket.
            The bucket, its terms, and any other agents using it are <b>not affected</b>.
          </p>
          <p style={{ fontSize: 13, color: colors.slate[500], marginBottom: 0 }}>
            You can re-attach it any time from the same screen.
          </p>
        </div>
      ),
      okText: 'Detach',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detail.detachPhraseGroup(row.linkId);
          message.success(`Detached "${row.title}" from ${agentName}`);
          trackEvent('AgentPhraseList.Detached', {
            agentId: detail.agentId ?? -1,
            groupId: row.groupId,
            linkId: row.linkId,
          });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to detach phrase bucket');
        }
      },
    });
  }

  const columns: ColumnsType<AgentPhraseGroupView> = [
    {
      title: 'Bucket',
      key: 'title',
      render: (_t, row) => (
        <div>
          <Text strong style={{ fontSize: 13.5 }}>{row.title}</Text>
          {row.merchantId == null && (
            <span style={{ marginLeft: 8 }}>
              <StatusPill tone="violet">Global</StatusPill>
            </span>
          )}
          {row.description && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Terms',
      key: 'terms',
      width: 120,
      render: (_t, row) => <StatusPill tone="ink">{row.termCount} term{row.termCount === 1 ? '' : 's'}</StatusPill>,
    },
    {
      title: 'Bucket id',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 120,
      render: (v: number) => <Mono>#{v}</Mono>,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      render: (_t, row) => (
        <Tooltip title="Detach this bucket from the agent (does not delete the bucket)">
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => confirmDetach(row)}>
            Detach
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      {!enabled && detail.phraseGroups.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Phrase list is turned off for this agent"
          description="These buckets are attached but won't bias voice transcription until you enable the Phrase list toggle on the Overview tab."
        />
      )}
      <SectionCard
        title="Phrase Lists"
        subtitle={
          detail.phraseGroups.length === 0
            ? 'No phrase buckets attached yet. Click Attach buckets to add one.'
            : `${detail.phraseGroups.length} bucket${detail.phraseGroups.length === 1 ? '' : 's'} biasing this agent's voice transcription`
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setLinkDrawerOpen(true);
              trackEvent('AgentPhraseList.AttachDrawerOpened', { agentId: detail.agentId ?? -1 });
            }}
          >
            Attach buckets
          </Button>
        }
        noPadding={detail.phraseGroups.length > 0}
      >
        {detail.phraseGroups.length === 0 ? (
          <BrandedEmpty
            icon={<AudioOutlined />}
            title="No phrase buckets attached"
            description="Phrase buckets bias speech-to-text toward this agent's domain vocabulary (drug names, regimens, symptoms). Click Attach buckets above to add one."
          />
        ) : (
          <Table<AgentPhraseGroupView>
            rowKey="linkId"
            columns={columns}
            dataSource={detail.phraseGroups}
            pagination={false}
            size="middle"
            scroll={{ x: 'max-content' }}
          />
        )}
      </SectionCard>

      <LinkPhraseBucketsDrawer
        open={linkDrawerOpen}
        onClose={() => setLinkDrawerOpen(false)}
        alreadyLinkedIds={linkedIds}
        agentDisplayName={agentName}
      />
    </>
  );
});

const ProceduresTab = observer(function ProceduresTab() {
  const { agentDetailStore: detail } = useStore();
  // In-place skill viewer — clicking a skill name opens the full
  // Description + Instructions in a 90% drawer instead of navigating
  // the user away to /skills/{id}.
  const [openSkillId, setOpenSkillId] = useState<number | null>(null);
  const [openSkillPreview, setOpenSkillPreview] = useState<AiSkill | null>(null);
  // Skill history viewer — clock icon in the Version column opens this.
  const [historySkillId, setHistorySkillId] = useState<number | null>(null);
  const [historySkillName, setHistorySkillName] = useState<string | null>(null);

  if (detail.isLoadingProcedural && detail.proceduralSkills.length === 0) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.proceduralError) {
    return (
      <SectionCard>
        <Alert type="error" message="Failed to load skills" description={detail.proceduralError} showIcon />
      </SectionCard>
    );
  }
  if (detail.proceduralSkills.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ToolOutlined />}
          title="No skills linked yet"
          description="Skills are reusable capabilities this agent loads on demand for complex workflows."
          action={
            <Link to="/skills">
              <Button>Browse the skills library</Button>
            </Link>
          }
        />
      </SectionCard>
    );
  }

  const columns: ColumnsType<AiSkill> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row) => (
        // Click the name → open the skill content drawer in place. Keeping
        // the visual treatment (bold + ink color + monospace name) so it
        // still reads as the primary identifier.
        <button
          type="button"
          onClick={() => {
            setOpenSkillId(row.id);
            setOpenSkillPreview(row);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: colors.ink[600],
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 13.5,
            textAlign: 'left',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
          title="Click to view full instructions"
        >
          {name}
        </button>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat?: string) =>
        cat ? <StatusPill tone="ink">{cat}</StatusPill> : <StatusPill tone="slate">—</StatusPill>,
    },
    {
      title: 'Always-load',
      dataIndex: 'alwaysLoad',
      key: 'alwaysLoad',
      width: 130,
      render: (v?: boolean) =>
        v ? <StatusPill tone="green">Always</StatusPill> : <StatusPill tone="slate">On demand</StatusPill>,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 130,
      render: (v: string | undefined, row) => (
        <Space size={4}>
          <Mono>{v ?? '—'}</Mono>
          <Tooltip title="View edit history and compare versions">
            <Button
              type="text"
              size="small"
              icon={<HistoryOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setHistorySkillId(row.id);
                setHistorySkillName(row.name ?? null);
              }}
              aria-label="View history"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <SectionCard noPadding>
      <Table<AiSkill>
        rowKey="id"
        columns={columns}
        dataSource={detail.proceduralSkills}
        pagination={false}
        size="middle"
      />

      <SkillContentDrawer
        skillId={openSkillId}
        preview={openSkillPreview}
        onClose={() => {
          setOpenSkillId(null);
          setOpenSkillPreview(null);
        }}
        onOpenHistory={(s) => {
          setHistorySkillId(s.id);
          setHistorySkillName(s.name ?? null);
        }}
      />

      <SkillHistoryDrawer
        skillId={historySkillId}
        skillName={historySkillName}
        onClose={() => {
          setHistorySkillId(null);
          setHistorySkillName(null);
        }}
      />
    </SectionCard>
  );
});

const A2ATab = observer(function A2ATab() {
  const { agentDetailStore: detail } = useStore();
  if (detail.isLoadingA2A && detail.a2aSkills.length === 0) {
    return <SectionCard><CenterSpin /></SectionCard>;
  }
  if (detail.a2aError) {
    return (
      <SectionCard>
        <Alert type="error" message="Failed to load discoverable abilities" description={detail.a2aError} showIcon />
      </SectionCard>
    );
  }
  if (detail.a2aSkills.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<GlobalOutlined />}
          title="No discoverable abilities advertised"
          description="A2A skills let other agents route to this one via tags. Useful when this agent specializes in a domain."
        />
      </SectionCard>
    );
  }

  const columns: ColumnsType<AiA2AAgentSkill> = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (n: string) => <Text strong>{n}</Text> },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (t?: string) => <Text type="secondary" style={{ fontSize: 13 }}>{t ?? '—'}</Text>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 240,
      render: (tags?: string) => {
        const list = (tags ?? '').split(',').map((s) => s.trim()).filter(Boolean);
        return list.length === 0 ? (
          <StatusPill tone="slate">—</StatusPill>
        ) : (
          <Space size={4} wrap>
            {list.map((t) => (
              <StatusPill key={t} tone="ink">{t}</StatusPill>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v: boolean) =>
        v === false ? <StatusPill tone="slate">Inactive</StatusPill> : <StatusPill tone="green">Active</StatusPill>,
    },
  ];

  return (
    <SectionCard noPadding>
      <Table<AiA2AAgentSkill>
        rowKey="id"
        columns={columns}
        dataSource={detail.a2aSkills}
        pagination={false}
        size="middle"
      />
    </SectionCard>
  );
});

const PeersTab = observer(function PeersTab() {
  const { agentDetailStore: detail } = useStore();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AiAgentConsultingPeer | null>(null);

  function openEditor(row: AiAgentConsultingPeer | null): void {
    setEditing(row);
    setEditorOpen(true);
    trackEvent('Peer.EditorOpened', { peerId: row?.id ?? 'new' });
  }

  function handleDelete(row: AiAgentConsultingPeer): void {
    Modal.confirm({
      title: 'Remove this peer link?',
      content: `"${row.peerAgentName ?? `Agent #${row.peerAgentId}`}" will no longer be consultable from this agent.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detail.removePeer(row.id);
          message.success('Peer removed');
          trackEvent('Peer.Removed', { peerId: row.id });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to remove peer');
        }
      },
    });
  }

  const headerActions = (
    <Space>
      <Button icon={<ReloadOutlined />} onClick={() => void detail.refreshPeers()}>
        Refresh
      </Button>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
        Add peer
      </Button>
    </Space>
  );

  if (detail.isLoadingPeers && detail.peers.length === 0) {
    return (
      <SectionCard
        title="Peer agents"
        subtitle="Other agents this one can consult during a conversation."
        actions={headerActions}
      >
        <CenterSpin />
      </SectionCard>
    );
  }

  if (detail.peersError) {
    return (
      <SectionCard
        title="Peer agents"
        subtitle="Other agents this one can consult during a conversation."
        actions={headerActions}
      >
        <Alert type="error" message="Failed to load peers" description={detail.peersError} showIcon />
      </SectionCard>
    );
  }

  if (detail.peers.length === 0) {
    return (
      <>
        <SectionCard
          title="Peer agents"
          subtitle="Other agents this one can consult during a conversation."
          actions={headerActions}
        >
          <BrandedEmpty
            icon={<TeamOutlined />}
            title="No peer agents configured"
            description="Peers let this agent ask domain specialists for help mid-conversation. Pick one to get started."
            action={
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => openEditor(null)}>
                Add the first peer
              </Button>
            }
          />
        </SectionCard>
        <PeerEditorDrawer open={editorOpen} peer={editing} onClose={() => setEditorOpen(false)} />
      </>
    );
  }

  const columns: ColumnsType<AiAgentConsultingPeer> = [
    {
      title: 'Peer agent',
      key: 'peer',
      render: (_t, row) => (
        <Link
          to={`/agents/${row.peerAgentId}`}
          style={{ color: colors.ink[600], fontWeight: 600, textDecoration: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          {row.peerAgentName ?? `Agent #${row.peerAgentId}`}
        </Link>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      ellipsis: true,
      render: (s?: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {s ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Exclusion guidance',
      dataIndex: 'exclusionGuidance',
      key: 'exclusionGuidance',
      ellipsis: true,
      render: (s?: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {s ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v?: boolean) =>
        v === false ? (
          <StatusPill tone="slate">Inactive</StatusPill>
        ) : (
          <StatusPill tone="green">Active</StatusPill>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_t, row) => (
        <Space size={2}>
          <Tooltip title="Edit scope / exclusion / status">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditor(row)}
            />
          </Tooltip>
          <Tooltip title="Remove this peer link">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="Peer agents"
        subtitle={`${detail.peers.length} peer${detail.peers.length === 1 ? '' : 's'} this agent can consult`}
        actions={headerActions}
        noPadding
      >
        <Table<AiAgentConsultingPeer>
          rowKey="id"
          columns={columns}
          dataSource={detail.peers}
          pagination={false}
          size="middle"
          onRow={(row) => ({
            onClick: () => openEditor(row),
            style: { cursor: 'pointer' },
          })}
        />
      </SectionCard>
      <PeerEditorDrawer open={editorOpen} peer={editing} onClose={() => setEditorOpen(false)} />
    </>
  );
});

// ── Prebuilt prompts tab ────────────────────────────────────────────────────

const PrebuiltPromptsTab = observer(function PrebuiltPromptsTab() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [pinDrawerOpen, setPinDrawerOpen] = useState(false);

  function handleUnpin(linkId: number, label: string): void {
    Modal.confirm({
      title: 'Unpin this prompt?',
      content: `"${label}" will no longer appear as a chip above this agent's chat.`,
      okText: 'Unpin',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detail.unpinPrompt(linkId);
          message.success('Unpinned');
          trackEvent('Agent.PromptUnpinned', { linkId });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to unpin');
        }
      },
    });
  }

  const headerActions = (
    <Space>
      <Button icon={<ReloadOutlined />} onClick={() => void detail.refreshPinnedPrompts()}>
        Refresh
      </Button>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setPinDrawerOpen(true)}>
        Pin prompts
      </Button>
    </Space>
  );

  if (detail.isLoadingPinnedPrompts && detail.pinnedPrompts.length === 0) {
    return (
      <SectionCard
        title="Prebuilt prompts"
        subtitle="One-click chat chips this agent surfaces above its input."
        actions={headerActions}
      >
        <CenterSpin />
      </SectionCard>
    );
  }

  if (detail.pinnedPromptsError) {
    return (
      <SectionCard
        title="Prebuilt prompts"
        subtitle="One-click chat chips this agent surfaces above its input."
        actions={headerActions}
      >
        <Alert
          type="error"
          message="Failed to load pinned prompts"
          description={detail.pinnedPromptsError}
          showIcon
        />
      </SectionCard>
    );
  }

  if (detail.pinnedPrompts.length === 0) {
    return (
      <>
        <SectionCard
          title="Prebuilt prompts"
          subtitle="One-click chat chips this agent surfaces above its input."
          actions={headerActions}
        >
          <BrandedEmpty
            icon={<ThunderboltOutlined />}
            title="No prompts pinned yet"
            description="Pinning a prebuilt prompt makes it appear as a chip above this agent's chat input. Users click → instructions get sent."
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setPinDrawerOpen(true)}>
                Pin the first prompt
              </Button>
            }
          />
        </SectionCard>
        <PinPromptDrawer open={pinDrawerOpen} onClose={() => setPinDrawerOpen(false)} />
      </>
    );
  }

  return (
    <>
      <SectionCard
        title="Prebuilt prompts"
        subtitle={`${detail.pinnedPrompts.length} prompt${detail.pinnedPrompts.length === 1 ? '' : 's'} pinned to this agent. Order in chat matches DisplayOrder.`}
        actions={headerActions}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {detail.pinnedPrompts.map((link) => {
            const p = link.prebuiltPrompt;
            const label = p?.description ?? p?.name ?? `Prompt #${link.prebuiltPromptId}`;
            const chipText = p?.buttonText ?? p?.description ?? 'Untitled';
            return (
              <div
                key={link.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 14px',
                  background: isDark ? colors.slate[800] : '#FFFFFF',
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isDark ? `${colors.ink[700]}55` : colors.ink[50],
                    color: colors.ink[600],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    flexShrink: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                  }}
                >
                  {link.displayOrder + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: isDark ? colors.slate[50] : colors.slate[900],
                      marginBottom: 4,
                    }}
                  >
                    {label}
                  </div>
                  {p?.buttonText && (
                    <div
                      style={{
                        display: 'inline-block',
                        fontSize: 11.5,
                        fontFamily: "'JetBrains Mono', monospace",
                        color: isDark ? colors.slate[300] : colors.slate[600],
                        background: isDark ? colors.slate[700] : colors.slate[100],
                        padding: '2px 8px',
                        borderRadius: 999,
                        marginBottom: 6,
                      }}
                    >
                      Chip: "{chipText}"
                    </div>
                  )}
                  {p?.instructions && (
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {p.instructions}
                    </Text>
                  )}
                </div>
                <Space direction="vertical" align="end" size={4}>
                  <Tooltip title="Unpin from this agent">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleUnpin(link.id, label)}
                    />
                  </Tooltip>
                  <Link
                    to={`/prompts`}
                    style={{
                      fontSize: 11,
                      color: isDark ? colors.slate[400] : colors.slate[500],
                    }}
                  >
                    Edit prompt →
                  </Link>
                </Space>
              </div>
            );
          })}
        </div>
      </SectionCard>
      <PinPromptDrawer open={pinDrawerOpen} onClose={() => setPinDrawerOpen(false)} />
    </>
  );
});

const AdvancedTab = observer(function AdvancedTab() {
  const { agentDetailStore: detail } = useStore();
  const a = detail.agent;
  return (
    <SectionCard title="Advanced" subtitle="Tuning knobs that shape conversation length, depth, and creativity.">
      <DefList
        rows={[
          { label: 'Consultation enabled', value: a?.enableConsultation ? <StatusPill tone="green">On</StatusPill> : <StatusPill tone="slate">Off</StatusPill> },
          { label: 'Max consultation depth', value: a?.maxConsultationDepth ?? '—' },
          { label: 'Memory strategy', value: 'Sliding Window (default)' },
          { label: 'Creativity', value: 'Low / clinical-safe (default)' },
        ]}
        singleColumn
      />
    </SectionCard>
  );
});

const DeployTab = function DeployTab() {
  return (
    <SectionCard
      title="Deploy"
      subtitle="Validation gates and ship-readiness. Hooks into the validation framework."
    >
      <BrandedEmpty
        icon={<DeploymentUnitOutlined />}
        title="Deploy workflow lands in H2"
        description="When skill routing, validation, and stage-gates are wired, this tab will show the readiness checklist and Promote button."
      />
    </SectionCard>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function CenterSpin() {
  return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <Spin />
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{children}</span>
  );
}

function AudienceCell({ agent }: { agent: AgentDefinition }) {
  return (
    <Space size={4}>
      {agent.patientApp && <StatusPill tone="ink">Patient</StatusPill>}
      {agent.merchantApp && <StatusPill tone="violet">Care team</StatusPill>}
      {!agent.patientApp && !agent.merchantApp && <StatusPill tone="slate">—</StatusPill>}
    </Space>
  );
}

function formatDate(d?: string): string | null {
  if (!d) return null;
  try {
    const date = new Date(d);
    return date.toLocaleString();
  } catch {
    return d;
  }
}

const DefList = observer(function DefList({
  rows,
  singleColumn,
}: {
  rows: { label: string; value: React.ReactNode; span?: number }[];
  singleColumn?: boolean;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: singleColumn ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
        rowGap: 12,
        columnGap: 24,
        marginTop: 4,
      }}
    >
      {rows.map((r) => (
        <div key={r.label} style={{ gridColumn: r.span === 2 ? '1 / -1' : undefined }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isDark ? colors.slate[500] : colors.slate[400],
              marginBottom: 4,
            }}
          >
            {r.label}
          </div>
          <div style={{ fontSize: 13.5, color: isDark ? colors.slate[200] : colors.slate[800] }}>{r.value}</div>
        </div>
      ))}
    </div>
  );
});

